
import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Button,
  Space,
  Typography,
  message as antMessage,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import api from "../../core/axios/axiosInstance";
import { fetchProfile } from "../../core/data/redux/slices/ProfileSlice";

const { Text } = Typography;

const ProfileCompletionModal = () => {
  const profile = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [parsedPhone, setParsedPhone] = useState(null);


  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [welcomeFormData, setWelcomeFormData] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "971",
  });
  useEffect(() => {
    let interval;

    if (showOtpField && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    if (timer === 0) {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [showOtpField, timer]);



  // Check if profile is incomplete (any of the three fields missing)
  // Only show for companyAdmin role
  useEffect(() => {
    const hasFirstname = profile.firstname && profile.firstname.trim() !== "";
    const hasLastname = profile.lastname && profile.lastname.trim() !== "";
    const hasPhone = profile.phonenumbers && profile.phonenumbers.length > 0;
    const isCompanyAdmin = profile.role === "companyAdmin";

    // Show modal if ANY of the three are missing AND user is companyAdmin
    if ((!hasFirstname || !hasPhone) && !profile.isLoading && isCompanyAdmin) {
      setShowWelcomeModal(true);

      // Pre-fill existing values
      let phoneValue = "";
      if (hasPhone) {
        const phoneData = profile.phonenumbers[0];
        // Check if it's an object with countryCode and number, or just a string
        if (
          typeof phoneData === "object" &&
          phoneData.countryCode &&
          phoneData.number
        ) {
          phoneValue = `${phoneData.countryCode}${phoneData.number}`;
        } else if (typeof phoneData === "string") {
          phoneValue = phoneData;
        }
      }

      setWelcomeFormData({
        firstname: profile.firstname || "",
        lastname: profile.lastname || "",
        phonenumber: phoneValue,
      });
    } else {
      setShowWelcomeModal(false);
    }
  }, [profile]);

  // Handle welcome form input changes
  const handleWelcomeFormChange = (e) => {
    const { name, value } = e.target;
    setWelcomeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle phone input change
  const handleWelcomePhoneChange = (value) => {
    setWelcomeFormData((prev) => ({
      ...prev,
      phonenumber: value,
    }));
  };

  // Helper function to parse phone number using libphonenumber-js
  const parsePhoneNumber = (phoneString) => {
    try {
      // Phone string from react-phone-input-2 comes as "971501234567"
      // We need to add + prefix for libphonenumber-js
      const phoneWithPlus = phoneString.startsWith("+")
        ? phoneString
        : `+${phoneString}`;
      const phoneNumber = parsePhoneNumberFromString(phoneWithPlus);

      if (phoneNumber && phoneNumber.isValid()) {
        return {
          countryCode: phoneNumber.countryCallingCode,
          number: phoneNumber.nationalNumber,
        };
      } else {
        // Fallback: assume first 1-3 digits are country code
        for (let len = 3; len >= 1; len--) {
          const possibleCode = phoneString.substring(0, len);
          if (/^\d+$/.test(possibleCode) && phoneString.length > len) {
            return {
              countryCode: possibleCode,
              number: phoneString.substring(len),
            };
          }
        }
        return { countryCode: "", number: phoneString };
      }
    } catch (error) {
      console.warn("Phone parsing error:", error);
      // Fallback: assume first 2 digits are country code
      return {
        countryCode: phoneString.substring(0, 2),
        number: phoneString.substring(2),
      };
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleVerifyOtp = async () => {
    const finalOtp = otp.join("");

    if (finalOtp.length !== 6) {
      antMessage.error("Please enter 6 digit OTP");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await api.post("user/verify-phone-number", {
        countryCode: parsedPhone.countryCode,
        phonenumber: parsedPhone.number,
        otp: finalOtp,
      });

      if (response.data.status === "success") {
        antMessage.success("Phone verified! Your free trial has started.");

        const formData = new FormData();
        formData.append("firstname", welcomeFormData.firstname);
        formData.append("lastname", welcomeFormData.lastname);

        await api.put("/editProfile", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        api.post("user/sendDemoEmail");

        await dispatch(fetchProfile());
        setShowWelcomeModal(false);
      } else {
        antMessage.error(response.data.message);
      }
    } catch (error) {
      antMessage.error(
        error.response?.data?.message || "Invalid OTP"
      );
    } finally {
      setOtpLoading(false);
    }
  };
  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      const response = await api.post("user/verify-phone-number", {
        countryCode: parsedPhone.countryCode,
        phonenumber: parsedPhone.number,
        resendOtp: true,
      });

      if (response.data.status === "pending") {
        antMessage.success("OTP resent successfully");
        setTimer(60);
        setCanResend(false);
      } else {
        antMessage.error(response.data.message);
      }
    } catch (error) {
      antMessage.error("Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };
  const handleChangeNumber = () => {
    setShowOtpField(false);
    setOtp(["", "", "", "", "", ""]);
    setTimer(60);
  };

  const handleSendOtp = async () => {
    if (!welcomeFormData.phonenumber) {
      antMessage.error("Please enter phone number");
      return;
    }
    // setShowOtpField(true);
    const phoneObj = parsePhoneNumber(welcomeFormData.phonenumber);
    setParsedPhone(phoneObj);

    setIsSubmitting(true);
    try {
      const response = await api.post("user/verify-phone-number", {
        countryCode: phoneObj.countryCode,
        phonenumber: phoneObj.number,
      });

      if (response.data.status === "pending") {
        antMessage.success("OTP sent successfully");

        setShowOtpField(true);
        setTimer(60);
        setCanResend(false);
      } else {
        antMessage.error(response.data.message);
      }
    } catch (error) {
      antMessage.error(
        error.response?.data?.message || "Failed to send OTP"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // If current box has value → clear it
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // If empty → move to previous box
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);

        const prevInput = document.getElementById(`otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", paddingTop: "10px" }}>
          <h3 style={{ margin: 0, color: "#1890ff" }}>
            👋 Welcome to VOYCELL!
          </h3>
        </div>
      }
      open={showWelcomeModal}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={500}
    >
      <div style={{ padding: "20px 0" }}>
        <Text
          style={{
            display: "block",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          Let's complete your profile to get started
        </Text>

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              First Name <span style={{ color: "red" }}>*</span>
            </label>
            <Input
              size="large"
              name="firstname"
              value={welcomeFormData.firstname}
              onChange={handleWelcomeFormChange}
              placeholder="Enter your first name"
              maxLength={50}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Last Name
            </label>
            <Input
              size="large"
              name="lastname"
              value={welcomeFormData.lastname}
              onChange={handleWelcomeFormChange}
              placeholder="Enter your last name"
              maxLength={50}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Phone Number <span style={{ color: "red" }}>*</span>
            </label>
            <PhoneInput
              country={"ae"}
              value={welcomeFormData.phonenumber}
              onChange={handleWelcomePhoneChange}
              disabled={showOtpField}
              enableSearch
              inputProps={{
                name: "phone",
                required: true,
              }}

              inputStyle={{
                width: "100%",
                backgroundColor: showOtpField ? "#f5f5f5" : "#fff",
                color: showOtpField ? "rgba(0,0,0,0.45)" : "#000",
                cursor: showOtpField ? "not-allowed" : "text",
              }}
              buttonStyle={{
                backgroundColor: showOtpField ? "#f5f5f5" : "#fff",
                cursor: showOtpField ? "not-allowed" : "pointer",
              }}

              // buttonStyle={{
              //   border: "1px solid #d9d9d9",
              //   borderRight: "none",
              //   height: "40px",
              //   backgroundColor: "transparent",
              // }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          {/* <Button
            type="primary"
            size="large"
            block
            onClick={handleWelcomeFormSubmit}
            loading={isSubmitting}
            style={{ marginTop: "10px" }}
          >
            Continue
          </Button> */}
          {!showOtpField ? (
            <Button
              type="primary"
              size="large"
              block
              onClick={handleSendOtp}
              loading={isSubmitting}
            >
              Send OTP
            </Button>
          ) : (
            <>
              <Text style={{ display: "block", textAlign: "center", marginBottom: 15 }}>
                Enter 6 digit OTP sent to your Whatsapp
              </Text>

              {/* 6 OTP Boxes */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    id={`otp-${index}`}
                    value={digit}
                    maxLength={1}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    style={{
                      width: 45,
                      height: 45,
                      textAlign: "center",
                      fontSize: "18px",
                    }}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <Button
                type="primary"
                size="large"
                block
                onClick={handleVerifyOtp}
                loading={otpLoading}
                style={{ marginTop: 20 }}
              >
                Verify OTP
              </Button>

              {/* Timer / Resend */}
              <div style={{ textAlign: "center", marginTop: 10 }}>
                {canResend ? (
                  <Button type="link" onClick={handleResendOtp} loading={resendLoading}>
                    Resend OTP
                  </Button>
                ) : (
                  <Text type="secondary">Resend OTP in {timer}s</Text>
                )}
              </div>

              {/* Change Number */}
              <div style={{ textAlign: "center" }}>
                <Button type="link" onClick={handleChangeNumber}>
                  Change Phone Number
                </Button>
              </div>
            </>
          )}


        </Space>
      </div>
    </Modal>
  );
};

export default ProfileCompletionModal;